import React, { memo, useCallback, useMemo } from 'react'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import tw from 'twin.macro'
import { GridColumnMenuItemProps, useGridApiContext, useGridRootProps } from '@mui/x-data-grid-pro'
import { GSIcon } from 'src/components/shared/GSIcon'

const GridColumnMenuSortItemWithUnsortIconComponent = ({
  colDef,
  onClick,
}: GridColumnMenuItemProps) => {
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()

  const sortDirection = useMemo(() => {
    if (!colDef) {
      return null
    }

    const sortItem = apiRef.current.getSortModel().find((item) => {
      return item.field === colDef.field
    })

    return sortItem?.sort
  }, [apiRef, colDef])

  const sortingOrder = colDef.sortingOrder ?? rootProps.sortingOrder

  const onSortMenuItemClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      onClick(event)
      const direction = event.currentTarget.getAttribute('data-value') || null
      apiRef.current.sortColumn(colDef, direction === sortDirection ? null : (direction as any))
    },
    [apiRef, colDef, onClick, sortDirection],
  )

  const hasSortableOrder = sortingOrder.some((item) => {
    return !!item
  })

  if (!colDef || !colDef.sortable || !hasSortableOrder) {
    return null
  }

  return (
    <React.Fragment>
      {sortingOrder.includes('asc') && sortDirection !== 'asc' ? (
        <MenuItem onClick={onSortMenuItemClick} data-value="asc">
          <ListItemIcon>
            <rootProps.slots.columnMenuSortAscendingIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{apiRef.current.getLocaleText('columnMenuSortAsc')}</ListItemText>
        </MenuItem>
      ) : null}
      {sortingOrder.includes('desc') && sortDirection !== 'desc' ? (
        <MenuItem onClick={onSortMenuItemClick} data-value="desc">
          <ListItemIcon>
            <rootProps.slots.columnMenuSortDescendingIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{apiRef.current.getLocaleText('columnMenuSortDesc')}</ListItemText>
        </MenuItem>
      ) : null}
      {sortingOrder.includes(null) && sortDirection != null ? (
        <MenuItem onClick={onSortMenuItemClick}>
          <ListItemIcon>
            <GSIcon iconName="gs_icon_cross_large" css={tw`w-5 h-5`} />
          </ListItemIcon>
          <ListItemText>{apiRef.current.getLocaleText('columnMenuUnsort')}</ListItemText>
        </MenuItem>
      ) : null}
    </React.Fragment>
  )
}

export const GridColumnMenuSortItemWithUnsortIcon = memo(
  GridColumnMenuSortItemWithUnsortIconComponent,
)
